import { ACCEPTED_EXTENSIONS } from './types';

/**
 * Client-side ZIP expansion for batch intake.
 *
 * Use case: a user saves receipt photos into Google Photos through the
 * trimester, then downloads the album as a single ZIP at quarter-end and
 * drops it into the app. We extract the contained image / PDF entries
 * and feed them through the regular pipeline — same code path as if the
 * user had dropped the loose files individually.
 *
 * Filtering: only entries whose extension matches `ACCEPTED_EXTENSIONS`
 * (minus `.zip` itself — no recursive unzip) are returned. `__MACOSX/`
 * sidecar files, `.DS_Store`, and Windows `Thumbs.db` are dropped
 * because they're never receipts.
 *
 * The ZIP itself is read fully into memory by jszip — fine for the
 * gigabyte-scale quotas browsers offer, but if a user drops a 5 GB
 * archive of HEICs we'd OOM the tab. That's an extreme case; real
 * trimester batches are dozens of MBs.
 *
 * jszip is lazy-imported per CLAUDE.md convention #3 — first ZIP drop
 * pays the chunk-load cost, subsequent ones are cached.
 */

// Lowercased extensions accepted INSIDE a ZIP — same as the top-level
// ACCEPTED_EXTENSIONS minus `.zip` (no nested unzip — the user's
// expectation is "this ZIP holds receipts", not "this ZIP holds ZIPs").
const ACCEPTED_INNER_EXTENSIONS: ReadonlySet<string> = new Set(
  ACCEPTED_EXTENSIONS.filter((ext) => ext !== '.zip'),
);

const MIME_BY_EXT: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.pdf': 'application/pdf',
};

function getExt(name: string): string {
  const idx = name.lastIndexOf('.');
  if (idx === -1) return '';
  return name.slice(idx).toLowerCase();
}

export function isZipFile(file: File): boolean {
  if (
    file.type === 'application/zip' ||
    file.type === 'application/x-zip-compressed'
  ) {
    return true;
  }
  return file.name.toLowerCase().endsWith('.zip');
}

/**
 * Walk a list of dropped files. Pass-through any non-ZIP entry; for
 * each ZIP, lazy-load jszip, extract supported inner files into in-
 * memory `File` objects (so the rest of the pipeline can't tell they
 * came from an archive), and splice them into the result.
 *
 * Order is preserved: a ZIP's entries land in the slot where the ZIP
 * itself was, ahead of anything that came after the ZIP in the input.
 */
export async function expandZips(files: File[]): Promise<File[]> {
  const out: File[] = [];
  for (const file of files) {
    if (!isZipFile(file)) {
      out.push(file);
      continue;
    }
    const inner = await extractImagesFromZip(file);
    out.push(...inner);
  }
  return out;
}

async function extractImagesFromZip(file: File): Promise<File[]> {
  const { default: JSZip } = await import('jszip');
  let zip;
  try {
    zip = await JSZip.loadAsync(file);
  } catch (err) {
    // Corrupt ZIP, password-protected, etc. — drop the whole entry
    // rather than crashing the batch. The user gets nothing from the
    // ZIP but their other dropped files still process.
    console.warn(`[unzip] failed to read ${file.name}:`, err);
    return [];
  }

  const out: File[] = [];
  await Promise.all(
    Object.entries(zip.files).map(async ([path, entry]) => {
      if (entry.dir) return;
      // macOS / Windows sidecar metadata that ships in user-zipped folders.
      if (path.startsWith('__MACOSX/')) return;
      const baseName = path.split('/').pop() ?? path;
      if (baseName === '.DS_Store' || baseName === 'Thumbs.db') return;
      if (baseName.startsWith('.')) return; // hidden files
      const ext = getExt(baseName);
      if (!ACCEPTED_INNER_EXTENSIONS.has(ext)) return;
      const blob = await entry.async('blob');
      const mime = MIME_BY_EXT[ext] ?? blob.type;
      out.push(new File([blob], baseName, { type: mime }));
    }),
  );
  // Object.entries over zip.files iterates in archive order on every
  // jszip we've shipped, but the parallel Promise.all above resolves
  // out of order. Sort by name so the user sees a stable sequence —
  // matches what they'd get dragging the same files in from Finder.
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}
